import './ColumnDd.css'

import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { ColumnAdapter } from '@makyoapp/makyo-common'
import { Textfield } from '@makyoapp/makyo-icon/icon'
import { SelectDropdown } from '@makyoapp/makyo-ui'

import { filterFnReturnTypes } from '@/core/db/functions/helpers'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useGridV2Context } from '@/components/GridV2/contexts/GridV2Context'
import { RenderColumn, RenderColumnCustom } from '@/components/RenderColumn'

interface FieldColumnDdProps {
  /**
   * html id
   */
  id: string
  /**
   * placeholder string
   */
  placeholder?: string
  /**
   * allowed column type
   */
  types?: ColumnTypes[]
  /**
   * excluded column type
   * applied after allowed types
   */
  excludeTypes?: ColumnTypes[]
  /**
   * selected column
   */
  selected?: string
  /**
   * on change event
   *
   * @param column
   */
  onChange: (column: Pick<ColumnAdapter, 'id' | 'name' | 'type'>) => void
  renderOption?: (opt: Pick<ColumnAdapter, 'id' | 'name' | 'type'>, inToggle: boolean) => React.ReactNode
  withCustomValue?: boolean
  enterCustomPlaceholder?: string
  arrow?: boolean
  toggleClassName?: string
  hrPlaceholder?: string
  options?: ColumnAdapter[]
  showRowId?: boolean
  disabled?: boolean
  currentColumnId?: string
}

/**
 * show column selection options
 */
export function ColumnDd({
  id,
  placeholder,
  types = [],
  selected,
  onChange,
  renderOption,
  withCustomValue = false,
  enterCustomPlaceholder,
  arrow = true,
  toggleClassName,
  hrPlaceholder,
  excludeTypes = [],
  options: colOpts,
  showRowId = false,
  disabled = false,
  currentColumnId,
}: FieldColumnDdProps) {
  const { tables } = useDbClientContext()
  const { table } = useGridV2Context()
  const { t } = useTranslation()

  const firstOption: Pick<ColumnAdapter, 'name' | 'id' | 'type'> = {
    id: '',
    name: placeholder || t('Select Column'),
    type: ColumnTypes.Text,
  }

  const enterCustomOption: Pick<ColumnAdapter, 'name' | 'id' | 'type'> = {
    id: '',
    name: enterCustomPlaceholder || t('Enter custom'),
    type: ColumnTypes.Text,
  }

  const customOption = withCustomValue ? enterCustomOption : firstOption

  const columnOptions = useMemo(() => {
    let cols = (showRowId ? [{ id: '_id', name: 'Row ID', type: ColumnTypes.RowID }] : []).concat(
      colOpts
        ? colOpts
        : table
          ? types?.length > 0
            ? table.columns.filter((tableCol) => {
                if (tableCol.type === ColumnTypes.Lookup) {
                  const connCol = table.columns.find((connCol) => {
                    return connCol.id === tableCol?.lookup?.connColId
                  })
                  if (connCol) {
                    const connStg = connCol.connections
                    const foreignTable = tables.find((tb) => tb.id === connStg?.fTableId)
                    const foreignColumn = foreignTable?.columns.find((fc) => fc.id === tableCol?.lookup?.colId)
                    return foreignColumn && types.includes(foreignColumn.type)
                  }
                }

                if (tableCol.type === ColumnTypes.Function) {
                  return filterFnReturnTypes(tableCol, types, true)
                }

                return types?.includes(tableCol.type)
              })
            : table.columns
          : []
    )

    if (excludeTypes?.length > 0) {
      cols = cols.filter((tc) => !excludeTypes?.includes(tc.type))
    }

    if (currentColumnId) {
      cols = cols.filter((tc) => tc.id !== currentColumnId)
    }

    return cols
  }, [colOpts, currentColumnId, excludeTypes, showRowId, table, tables, types])

  const options = table
    ? [customOption, `#{${hrPlaceholder || t('Columns')}}#` as never, ...columnOptions]
    : [customOption, `#{${hrPlaceholder || t('Columns')}}#` as never]

  const value = selected ? options.find((o) => o.id === selected) || options[0] : options[0]

  return (
    <SelectDropdown
      id={id}
      menuDefaultSize={false}
      menuClassName={'max-h-[220px] overflow-y-auto'}
      menuContainerClassName='mk-ag-sdd'
      toggleClassName={toggleClassName}
      menuHeadingClassName='px-4! py-2 text-light border-t bdr'
      menuZIndex='z-305'
      value={value}
      onChange={onChange}
      optionLabel={(opt) => opt.name}
      withSearch
      portalMenu
      arrow={arrow}
      renderOption={(opt, inToggle) => {
        return renderOption ? (
          renderOption(opt, inToggle)
        ) : opt.id === '' && withCustomValue ? (
          <RenderColumnCustom name={opt.name} icon={<Textfield className='fill-primary-500' />} />
        ) : (
          <RenderColumn col={opt} />
        )
      }}
      options={options}
      disabled={disabled}
    />
  )
}
